<template>
    <!-- Reusable marketing-spotlight band. Two-column layout: a branded
         16:9 slide on one side, headline + blurb + optional bullets +
         optional CTA on the other. `flip` swaps the columns so consecutive
         placements can zig-zag for visual rhythm. Asset slug maps to
         /img/spotlights/{slug}-{480,800,1200,1600}.{webp,jpg}.

         Default tone is `wash` - a soft pale-indigo gradient that always
         contrasts with the mkt-white / mkt-light sections every marketing
         page is built from, so a spotlight never visually merges into its
         neighbours regardless of placement. Top + bottom gradient hairlines
         further mark the band boundaries. -->
    <section v-intersection="revealCfg"
             :class="['mkt-spotlight-band', `mkt-spotlight-band--${tone}`, 'reveal-fade-up']"
             :style="`padding:${verticalPadding}px 0;`">
        <div class="mkt-container">
            <div class="row q-col-gutter-xl items-center" :class="flip ? 'reverse' : ''">
                <!-- Image column. col-md-7 / col-md-5 split puts more space on
                     the visual side since the slides have burned-in headlines
                     of their own. -->
                <div class="col-12 col-md-7">
                    <div class="mkt-spotlight-band-imgwrap">
                        <picture>
                            <source type="image/webp"
                                    :srcset="`/img/spotlights/${slug}-480.webp 480w, /img/spotlights/${slug}-800.webp 800w, /img/spotlights/${slug}-1200.webp 1200w, /img/spotlights/${slug}-1600.webp 1600w`"
                                    sizes="(max-width: 767px) 100vw, 60vw" />
                            <img :src="`/img/spotlights/${slug}-1200.jpg`"
                                 :srcset="`/img/spotlights/${slug}-480.jpg 480w, /img/spotlights/${slug}-800.jpg 800w, /img/spotlights/${slug}-1200.jpg 1200w, /img/spotlights/${slug}-1600.jpg 1600w`"
                                 sizes="(max-width: 767px) 100vw, 60vw"
                                 :alt="alt"
                                 width="1600" height="900"
                                 loading="lazy"
                                 decoding="async"
                                 class="mkt-spotlight-band-img" />
                        </picture>
                    </div>
                </div>

                <!-- Content column. -->
                <div class="col-12 col-md-5">
                    <q-item v-if="eyebrowIcon || title" class="q-pa-none q-mb-md">
                        <q-item-section v-if="eyebrowIcon" avatar style="min-width:72px;">
                            <div class="mkt-icon-circle" :class="iconCircleClass">
                                <q-icon :name="eyebrowIcon" />
                            </div>
                        </q-item-section>
                        <q-item-section>
                            <q-item-label class="text-weight-bold" style="font-size:1.5rem;color:#0a0f1a;line-height:1.2;">{{ title }}</q-item-label>
                        </q-item-section>
                    </q-item>

                    <p v-if="blurb" class="text-grey-7 q-mb-lg" style="font-size:1rem;line-height:1.7;">{{ blurb }}</p>

                    <q-list v-if="bullets && bullets.length" dense class="mkt-feature-list q-mb-md">
                        <q-item v-for="(b, bi) in bullets" :key="bi" class="q-px-none">
                            <q-item-section avatar top style="min-width:28px;padding-right:0;">
                                <q-icon name="check_circle" color="positive" size="20px" />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label style="font-size:0.95rem;color:#333;line-height:1.55;">{{ b }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-list>

                    <q-btn v-if="ctaLabel && ctaHref"
                           outline no-caps
                           :label="ctaLabel"
                           :href="ctaHref"
                           icon-right="arrow_forward"
                           style="border-radius:8px;color:#4F46E5;border-color:#C7D2FE;font-weight:500;" />
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'MarketingSpotlight',
    mixins: [revealMixin],
    props: {
        // Asset slug. Resolves to /img/spotlights/{slug}-{w}.{webp,jpg} for
        // widths 480/800/1200/1600. Required.
        slug: { type: String, required: true },
        // Accessible alt text describing the image content. Required.
        alt: { type: String, required: true },
        // Headline displayed in the content column.
        title: { type: String, default: '' },
        // One-paragraph blurb under the headline.
        blurb: { type: String, default: '' },
        // Optional bullet list rendered with green check icons.
        bullets: { type: Array, default: () => [] },
        // Optional eyebrow icon shown left of the title in a tinted circle.
        eyebrowIcon: { type: String, default: '' },
        // CSS class controlling the eyebrow circle tint -
        // mkt-icon-circle--green/blue/gold/purple from the theme.
        iconCircleClass: { type: String, default: 'mkt-icon-circle--blue' },
        // Optional CTA button rendered below the bullets.
        ctaLabel: { type: String, default: '' },
        ctaHref: { type: String, default: '' },
        // Swap columns so consecutive placements can zig-zag.
        flip: { type: Boolean, default: false },
        // Section background tone:
        //   - 'wash'  (default) soft pale-indigo gradient - distinct from
        //             both mkt-white and mkt-light, so the spotlight never
        //             visually merges with adjacent page sections.
        //   - 'white' flat mkt-white - use sparingly when the spotlight needs
        //             to feel continuous with an adjacent white block.
        //   - 'light' flat mkt-light - same idea for light-gray contexts.
        tone: { type: String, default: 'wash' },
        // Top/bottom padding in px. 60 matches the existing Security.vue and
        // Features.vue section rhythm.
        verticalPadding: { type: Number, default: 72 }
    }
};
</script>

<style scoped>
.reverse { flex-direction: row-reverse; }

/* Spotlight band base. position:relative anchors the hairline pseudo-elements
   that visually separate the band from adjacent sections. */
.mkt-spotlight-band {
    position: relative;
}

/* Top + bottom gradient hairlines. Fade from transparent → soft indigo →
   transparent so the band has a clean edge without a hard border line. */
.mkt-spotlight-band::before,
.mkt-spotlight-band::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent 0%, rgba(99, 102, 241, 0.18) 25%, rgba(99, 102, 241, 0.18) 75%, transparent 100%);
    pointer-events: none;
}

.mkt-spotlight-band::before { top: 0; }
.mkt-spotlight-band::after  { bottom: 0; }

/* Default tone - soft pale-indigo gradient wash. Three-stop diagonal so the
   band feels distinct from every other marketing-section tone on the site
   (mkt-white #fff, mkt-light #f8fafc, mkt-dark navy). Tint is light enough
   that the burned-in slide artwork remains the visual focus. */
.mkt-spotlight-band--wash {
    background: linear-gradient(135deg, #F7F9FF 0%, #EEF1FE 50%, #F4F0FF 100%);
}

/* Escape hatches: same flat tones the rest of the site uses, in case a
   placement specifically wants to feel continuous with its neighbour. */
.mkt-spotlight-band--white { background: #ffffff; }
.mkt-spotlight-band--light { background: #f8fafc; }

/* Image wrap. The 16:9 slides ship with their own painted backgrounds and
   designed corners; the wrap adds a soft halo + slight lift so the slide
   feels anchored in the band rather than pasted onto a flat field. */
.mkt-spotlight-band-imgwrap {
    position: relative;
    border-radius: 18px;
    padding: 6px;
    background: linear-gradient(135deg, rgba(79, 70, 229, 0.10), rgba(124, 58, 237, 0.06));
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.14), 0 8px 20px rgba(15, 23, 42, 0.06);
}

.mkt-spotlight-band-img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 14px;
}

/* Compact the bullet list - the slide art carries the headline, so the
   right-column copy should breathe but not dominate. */
.mkt-spotlight-band :deep(.mkt-feature-list .q-item) {
    min-height: auto;
    padding-top: 4px;
    padding-bottom: 4px;
}
</style>
